import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getLocales } from 'expo-localization';
import AsyncStorage from '@react-native-async-storage/async-storage';

import en from './en';
import vi from './vi';
import ja from './ja';
import { translationsApi } from '../api/translations';

const LANGUAGE_STORAGE_KEY = 'cbjj_language';
const DB_CACHE_PREFIX = 'cbjj_db_translations_';
// DB-backed names (plans/classes/locations) live in their own namespace so a
// refresh can wholly REPLACE them (dropping keys the backend removed) without
// disturbing the static 'translation' bundle. t() falls back to it, so callers
// keep using t('plans.x.name') unchanged.
const DB_NS = 'db';

const resources = { en, vi, ja };

export type SupportedLanguage = keyof typeof resources;

export const SUPPORTED_LANGUAGES: { code: SupportedLanguage; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'vi', label: 'Tiếng Việt' },
  { code: 'ja', label: '日本語' },
];

function getDeviceLanguage(): SupportedLanguage {
  const locales = getLocales();
  const deviceLang = locales?.[0]?.languageCode ?? 'en';
  return deviceLang in resources ? (deviceLang as SupportedLanguage) : 'en';
}

let resolvedLanguage: SupportedLanguage = getDeviceLanguage();

// Guards last-selection-wins when the language is changed rapidly.
let languageChangeToken = 0;

// Turn a flat "namespace.machine_name.field" -> text map into the nested object
// addResourceBundle expects.
function nest(data: Record<string, string>): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [key, text] of Object.entries(data)) {
    if (!text) continue;
    const parts = key.split('.');
    if (parts.length < 3) continue;
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      node[parts[i]] = (node[parts[i]] as Record<string, unknown>) || {};
      node = node[parts[i]] as Record<string, unknown>;
    }
    node[parts[parts.length - 1]] = text;
  }
  return root;
}

// Replace the whole DB namespace in one operation. removeResourceBundle emits
// 'removed' and addResourceBundle emits 'added'; with bindI18nStore below this
// re-renders live components, so late-arriving or refreshed names update on
// screen instead of staying as raw keys — and dropped keys actually disappear.
function applyDbTranslations(lang: string, data: Record<string, string>) {
  if (i18n.hasResourceBundle(lang, DB_NS)) {
    i18n.removeResourceBundle(lang, DB_NS);
  }
  const nested = nest(data);
  if (Object.keys(nested).length > 0) {
    i18n.addResourceBundle(lang, DB_NS, nested, true, true);
  }
}

// Fetch fresh DB translations, apply them, and cache for instant next launch.
// A successful response is authoritative — apply it as-is (honouring removals)
// and cache it. Only a network failure (caught) leaves prior values in place.
async function refreshDbTranslations(lang: SupportedLanguage) {
  try {
    const dbTranslations = await translationsApi.getTranslations(lang);
    const data = dbTranslations.data ?? {};
    applyDbTranslations(lang, data);
    try {
      await AsyncStorage.setItem(DB_CACHE_PREFIX + lang, JSON.stringify(data));
    } catch {
      // cache write is best-effort
    }
  } catch {
    // DB translations are non-critical — cached/static values remain
  }
}

export async function initI18n() {
  try {
    const stored = await AsyncStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && stored in resources) {
      resolvedLanguage = stored as SupportedLanguage;
    }
  } catch {
    // ignore storage errors
  }

  await i18n.use(initReactI18next).init({
    resources,
    lng: resolvedLanguage,
    fallbackLng: 'en',
    ns: ['translation', DB_NS],
    defaultNS: 'translation',
    fallbackNS: DB_NS, // t('plans.x.name') resolves via the DB namespace
    interpolation: { escapeValue: false },
    compatibilityJSON: 'v4',
    // Re-render components when the DB namespace is added/removed at runtime
    react: { bindI18nStore: 'added removed' },
  });

  // Apply cached DB translations synchronously-ish so class/plan/location names
  // are correct on the very first paint of every launch after the first.
  try {
    const cached = await AsyncStorage.getItem(DB_CACHE_PREFIX + resolvedLanguage);
    if (cached) {
      applyDbTranslations(resolvedLanguage, JSON.parse(cached));
    }
  } catch {
    // ignore cache read/parse errors
  }

  // Refresh from the server in the background — do NOT block app startup on it.
  refreshDbTranslations(resolvedLanguage);

  return i18n;
}

export async function changeLanguage(lang: SupportedLanguage) {
  const token = ++languageChangeToken;

  // Apply cached translations for the new language immediately, then refresh.
  try {
    const cached = await AsyncStorage.getItem(DB_CACHE_PREFIX + lang);
    if (cached) {
      applyDbTranslations(lang, JSON.parse(cached));
    }
  } catch {
    // ignore
  }

  // A newer selection superseded this one while we awaited — abandon it so the
  // slower earlier pick can't clobber the user's latest choice.
  if (token !== languageChangeToken) return;

  await i18n.changeLanguage(lang);

  // Re-check after every await: a newer selection may have started meanwhile,
  // and the stale call must not persist the wrong preference or refresh it.
  if (token !== languageChangeToken) return;
  try {
    await AsyncStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
  } catch {
    // ignore storage errors
  }

  if (token !== languageChangeToken) return;
  await refreshDbTranslations(lang);
}

export default i18n;
